import type { Geometry, Position } from 'geojson';
import { Point } from 'types/api-responses';

function finiteCoordComponent(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

// Flatten GeoJSON coordinates into [lng, lat] pairs for bounding-box math.
function geometryPositions(geometry: Geometry): Position[] | undefined {
  switch (geometry.type) {
    case 'Point':
      return [geometry.coordinates];
    case 'MultiPoint':
      return geometry.coordinates.slice();
    case 'LineString':
      return geometry.coordinates.slice();
    case 'MultiLineString':
      return geometry.coordinates.flat();
    case 'Polygon':
      return geometry.coordinates.flat();
    case 'MultiPolygon':
      return geometry.coordinates.flat(2);
    case 'GeometryCollection': {
      const parts = geometry.geometries
        .map((g) => geometryPositions(g))
        .filter((p): p is Position[] => Boolean(p?.length));
      if (parts.length === 0) {
        return undefined;
      }
      return parts.flat();
    }
    default:
      return undefined;
  }
}

function calculateBounds(
  longitudes: number[],
  latitudes: number[]
): [Point, Point] | undefined {
  if (longitudes.length === 0 || latitudes.length === 0) {
    return undefined;
  }

  // Reduced, not spread: an imported GIS area runs to tens of thousands of vertices,
  // and `Math.min(...coords)` throws RangeError past the argument limit.
  const extreme = (values: number[], pick: (a: number, b: number) => number) =>
    values.reduce((best, value) => pick(best, value), values[0]);

  const minLng = extreme(longitudes, Math.min);
  const maxLng = extreme(longitudes, Math.max);
  const minLat = extreme(latitudes, Math.min);
  const maxLat = extreme(latitudes, Math.max);

  if (
    !Number.isFinite(minLng) ||
    !Number.isFinite(maxLng) ||
    !Number.isFinite(minLat) ||
    !Number.isFinite(maxLat)
  ) {
    return undefined;
  }

  // Same corner encoding as before: [minLng, maxLat], [maxLng, minLat]
  // (see useCalculateMapDimensions + type-curve-map fitBounds).
  return [
    [minLng, maxLat],
    [maxLng, minLat],
  ] as [Point, Point];
}

export function calculateGeometryBounds(
  geometry: Geometry | null | undefined
): [Point, Point] | undefined {
  if (!geometry || typeof geometry !== 'object') {
    return undefined;
  }

  const allCoords = geometryPositions(geometry);
  if (!allCoords?.length) {
    return undefined;
  }

  const validPairs: [number, number][] = [];
  for (const coord of allCoords) {
    if (!Array.isArray(coord) || coord.length < 2) {
      continue;
    }
    const lng = finiteCoordComponent(coord[0]);
    const lat = finiteCoordComponent(coord[1]);
    if (lng !== undefined && lat !== undefined) {
      validPairs.push([lng, lat]);
    }
  }

  if (validPairs.length === 0) {
    return undefined;
  }

  const longitudes = validPairs.map((coord) => coord[0]);
  const latitudes = validPairs.map((coord) => coord[1]);

  return calculateBounds(longitudes, latitudes);
}
